import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, MessageCircle, Hash } from 'lucide-react';
import { getChatMessages, sendChatMessage, markChatSeen } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { playNotificationSound, showBrowserNotification } from '../utils/notificationSound';

const TeamChat = ({ isOpen, onClose, onChatOpened }) => {
    const { user } = useAuth();
    const { addToast } = useToast();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const prevMessageCountRef = useRef(0);
    const lastTimestampRef = useRef(null);
    const isFirstFetchRef = useRef(true);

    const scrollToBottom = useCallback((instant = false) => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ 
                behavior: instant ? 'auto' : 'smooth',
                block: 'end'
            });
        }
    }, []);

    const fetchMessages = useCallback(async () => {
        try {
            const res = await getChatMessages();
            if (res.success) {
                const data = res.data;
                
                // Check for new messages from other users (skip first fetch)
                if (!isFirstFetchRef.current && lastTimestampRef.current && data.length > 0) {
                    const newFromOthers = data.filter(
                        m => {
                            const mTime = new Date(m.createdAt).getTime();
                            const lTime = lastTimestampRef.current.getTime();
                            // Message must be newer than last known message AND not from current user
                            const isNew = mTime > lTime;
                            const isOther = m.userId?._id !== user?._id && m.userId !== user?._id;
                            return isNew && isOther;
                        }
                    );

                    if (newFromOthers.length > 0) {
                        // Play sound once
                        playNotificationSound();
                        
                        const lastMsg = newFromOthers[newFromOthers.length - 1];
                        const senderName = lastMsg.userId?.name || 'Someone';
                        
                        // Show toast
                        addToast(lastMsg.message, 'chat', senderName);
                        
                        // Show browser notification if tab hidden
                        showBrowserNotification('💬 Team Chat', `${senderName}: ${lastMsg.message}`);
                    }
                }

                setMessages(data);
                isFirstFetchRef.current = false;

                // Update last known timestamp to the LATEST message in current data
                if (data.length > 0) {
                    lastTimestampRef.current = new Date(data[data.length - 1].createdAt);
                }

                if (data.length !== prevMessageCountRef.current) {
                    const isInitialLoad = prevMessageCountRef.current === 0;
                    prevMessageCountRef.current = data.length;
                    
                    // If it's the first load of messages, scroll instantly after a short delay for layout
                    // to ensure we're at the bottom even with the entrance animation
                    if (isInitialLoad) {
                        setTimeout(() => scrollToBottom(true), 150);
                    } else {
                        setTimeout(() => scrollToBottom(false), 100);
                    }
                }
            }
        } catch (err) {
            console.error('Failed to fetch chat messages:', err);
        } finally {
            setLoading(false);
        }
    }, [scrollToBottom, user]);

    // Lock background scroll when chat is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Fetch messages on open + poll every 3s + mark as seen
    useEffect(() => {
        if (!isOpen) {
            // Reset state when closing so it's fresh when re-opening
            prevMessageCountRef.current = 0;
            isFirstFetchRef.current = true;
            return;
        }
        setLoading(true);
        fetchMessages();
        // Mark chat as seen when opened
        markChatSeen().then(() => {
            if (onChatOpened) onChatOpened();
        }).catch(() => {});
        const interval = setInterval(fetchMessages, 3000);
        return () => clearInterval(interval);
    }, [isOpen, fetchMessages]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [isOpen]);

    const handleSend = async () => {
        if (!input.trim() || sending) return;
        const msg = input.trim();
        setInput('');
        setSending(true);
        try {
            const res = await sendChatMessage(msg);
            if (res.success) {
                setMessages(prev => [...prev, res.data]);
                prevMessageCountRef.current += 1;
                setTimeout(scrollToBottom, 100);
            }
        } catch (err) {
            console.error('Failed to send message:', err);
            setInput(msg); // restore on error
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Render message text with clickable order links
    const renderMessageText = (text) => {
        const parts = text.split(/(#\d+)/g);
        return parts.map((part, i) => {
            const match = part.match(/^#(\d+)$/);
            if (match) {
                return (
                    <button
                        key={i}
                        onClick={() => {
                            navigate(`/orders?search=${match[1]}`);
                            onClose();
                        }}
                        className="inline-flex items-center gap-0.5 text-blue-500 hover:text-blue-700 font-semibold hover:underline transition-colors"
                    >
                        <Hash className="w-3 h-3" />
                        {match[1]}
                    </button>
                );
            }
            return <span key={i}>{part}</span>;
        });
    };

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateSeparator = (dateStr) => {
        const d = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (d.toDateString() === yesterday.toDateString()) return 'Hier';
        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    // Group messages by date
    const getDateKey = (dateStr) => new Date(dateStr).toDateString();

    const isOwnMessage = (msg) => {
        return msg.userId?._id === user?._id || msg.userId === user?._id;
    };

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[70] transition-opacity"
                    onClick={onClose}
                />
            )}

            {/* Chat Panel */}
            <div
                className={`
                    fixed right-0 top-0 h-full w-full sm:w-[420px] z-[80]
                    bg-white/95 backdrop-blur-xl shadow-2xl
                    transform transition-transform duration-300 ease-in-out
                    flex flex-col border-l border-gray-200/50
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                `}
            >
                {/* Header */}
                <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-600 to-blue-500">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
                                <MessageCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-white font-bold text-lg leading-tight">Team Chat</h2>
                                <p className="text-blue-100 text-xs mt-0.5">
                                    {messages.length} message{messages.length !== 1 ? 's' : ''}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1 scrollbar-thin">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex flex-col items-center gap-3">
                                <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                                <p className="text-sm text-gray-400">Chargement...</p>
                            </div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <div className="w-16 h-16 mx-auto bg-indigo-50 rounded-2xl flex items-center justify-center mb-4">
                                    <MessageCircle className="w-8 h-8 text-indigo-300" />
                                </div>
                                <p className="text-gray-500 font-medium">Aucun message</p>
                                <p className="text-gray-400 text-sm mt-1">Soyez le premier à écrire !</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((msg, idx) => {
                                const showDate = idx === 0 || getDateKey(msg.createdAt) !== getDateKey(messages[idx - 1].createdAt);
                                const own = isOwnMessage(msg);
                                const showName = !own && (idx === 0 || messages[idx - 1].userId?._id !== msg.userId?._id);

                                return (
                                    <React.Fragment key={msg._id || idx}>
                                        {showDate && (
                                            <div className="flex items-center gap-3 my-3">
                                                <div className="flex-1 h-px bg-gray-200" />
                                                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                                    {formatDateSeparator(msg.createdAt)}
                                                </span>
                                                <div className="flex-1 h-px bg-gray-200" />
                                            </div>
                                        )}
                                        <div className={`flex ${own ? 'justify-end' : 'justify-start'} mb-1`}>
                                            <div className={`max-w-[80%] flex flex-col ${own ? 'items-end' : 'items-start'}`}>
                                                {showName && (
                                                    <p className="text-[11px] font-bold text-indigo-500 mb-0.5 px-1 uppercase tracking-tight">
                                                        {msg.userId?.name || 'Unknown'}
                                                    </p>
                                                )}
                                                <div
                                                    className={`
                                                        px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm
                                                        ${own
                                                            ? 'bg-indigo-600 text-white rounded-tr-none'
                                                            : 'bg-gray-100 text-gray-800 rounded-tl-none border border-gray-200/50'
                                                        }
                                                    `}
                                                >
                                                    <p className="break-words font-medium">{renderMessageText(msg.message)}</p>
                                                    <p className={`text-[10px] mt-1.5 ${own ? 'text-indigo-100' : 'text-gray-400'} text-right font-mono`}>
                                                        {formatTime(msg.createdAt)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Input Area */}
                <div className="flex-shrink-0 p-4 border-t border-gray-100 bg-white/80">
                    <div className="flex items-center gap-2 bg-gray-50 rounded-2xl px-4 py-1 border border-gray-200 focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Écrire un message..."
                            className="flex-1 bg-transparent py-2.5 text-sm text-gray-800 placeholder-gray-400 outline-none"
                            disabled={sending}
                        />
                        <button
                            onClick={handleSend}
                            disabled={!input.trim() || sending}
                            className={`
                                p-2 rounded-xl transition-all duration-200
                                ${input.trim()
                                    ? 'bg-gradient-to-r from-indigo-500 to-blue-500 text-white shadow-md shadow-blue-200 hover:shadow-lg hover:scale-105'
                                    : 'text-gray-300 cursor-not-allowed'
                                }
                            `}
                        >
                            <Send className={`w-4 h-4 ${input.trim() ? '' : ''}`} />
                        </button>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1.5 text-center">
                        Tapez <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">#1245</kbd> pour lier une commande
                    </p>
                </div>
            </div>
        </>
    );
};

export default TeamChat;
